/**
 * Coupons, the shop, and power-ups.
 *
 * Kate's economy, in her words:
 *   Starting purse  "every game you start with 50, so you can only buy one thing"
 *   Collecting      "there's also coupons around the map... To collect coupons, you run
 *                    through them."
 *   Respawn cost    "you can respawn in this game, but when you respawn, you lose 50 of
 *                    your coupons"
 *   Morph tool      "you buy something called a morph tool... It'll say morph tool bunny
 *                    underneath."
 *   Weapons         human only — "you can buy a sword. A wooden sword and wooden axe and
 *                    a spear."
 *   Food            "you can also buy dog food and cat food and human food"
 *   Power-up        "if you get supposedly 100 coupons, you can buy something that... makes
 *                    you stronger. So like I can jump higher, but it only lasts for 1 day
 *                    in the game."
 */
import { CYCLE_SECONDS } from '../world/dayNight'
import { SPECIES, type SpeciesId } from '../animals/species'

/** "Every game you start with 50." */
export const STARTING_COUPONS = 50
/** "When you respawn, you lose 50 of your coupons." */
export const RESPAWN_COST = 50
/** "If you get supposedly 100 coupons, you can buy something that makes you stronger." */
export const POWER_UP_COST = 100
/** "It only lasts for 1 day in the game" — one full 26-minute cycle. */
export const POWER_UP_SECONDS = CYCLE_SECONDS

export type ItemKind = 'morph' | 'weapon' | 'food' | 'powerup'

export interface ShopItem {
  id: string
  /** What the shop row reads. Kate: the morph tool "will say morph tool bunny underneath". */
  label: string
  kind: ItemKind
  cost: number
  /** Weapons are human-only, which is the whole point of the human. */
  humanOnly?: true
  /** For morph tools, the species bought. */
  species?: SpeciesId
  /** For food, how many hunger levels it restores. */
  feeds?: number
}

/** Kate's three wooden weapons, and nothing else. */
const WEAPONS: ShopItem[] = [
  { id: 'wooden-sword', label: 'Wooden sword', kind: 'weapon', cost: 30, humanOnly: true },
  { id: 'wooden-axe', label: 'Wooden axe', kind: 'weapon', cost: 30, humanOnly: true },
  { id: 'spear', label: 'Spear', kind: 'weapon', cost: 40, humanOnly: true },
]

/** Her three foods. Amounts are an assumption; the three kinds are hers. */
const FOOD: ShopItem[] = [
  { id: 'human-food', label: 'Human food', kind: 'food', cost: 10, feeds: 5 },
  { id: 'dog-food', label: 'Dog food', kind: 'food', cost: 8, feeds: 4 },
  { id: 'cat-food', label: 'Cat food', kind: 'food', cost: 8, feeds: 4 },
]

const POWER_UP: ShopItem = {
  id: 'power-up', label: 'Power-up (1 day)', kind: 'powerup', cost: POWER_UP_COST,
}

/** Damage a bought weapon adds to the human's bite. */
const WEAPON_STRENGTH: Record<string, number> = {
  'wooden-sword': 3,
  'wooden-axe': 3,
  spear: 4,
}

/** The power-up's effect: "it makes you stronger... I can jump higher." */
export const POWER_UP_STRENGTH = 2
export const POWER_UP_JUMP_MULTIPLIER = 1.4

export interface Purchase {
  ok: boolean
  reason?: 'not-enough-coupons' | 'human-only' | 'already-owned' | 'wrong-biome'
}

export class Economy {
  coupons = STARTING_COUPONS
  /** Weapon ids the player owns. */
  readonly weapons = new Set<string>()
  /** Food ids held, with counts. */
  readonly pantry = new Map<string, number>()
  /** Seconds left on the power-up, 0 when inactive. */
  powerUpRemaining = 0

  /** Every item on offer, given who you currently are and where. */
  catalogue(biome: string): ShopItem[] {
    const morphs: ShopItem[] = Object.values(SPECIES)
      .filter((s) => s.id !== 'human' && (s.biome === 'any' || s.biome === biome))
      .map((s) => ({
        id: `morph-${s.id}`,
        // Kate's exact labelling: the species name sits under "morph tool".
        label: `Morph tool: ${s.name.toLowerCase()}`,
        kind: 'morph' as const,
        cost: s.cost,
        species: s.id,
      }))
    return [...morphs, ...WEAPONS, ...FOOD, POWER_UP]
  }

  canAfford(item: ShopItem): boolean {
    return this.coupons >= item.cost
  }

  /** Attempt a purchase. Returns why it failed rather than silently doing nothing. */
  buy(item: ShopItem, speciesId: SpeciesId, biome: string): Purchase {
    if (item.humanOnly && speciesId !== 'human') return { ok: false, reason: 'human-only' }
    if (item.kind === 'weapon' && this.weapons.has(item.id)) {
      return { ok: false, reason: 'already-owned' }
    }
    if (item.species) {
      const species = SPECIES[item.species]
      if (species.biome !== 'any' && species.biome !== biome) {
        return { ok: false, reason: 'wrong-biome' }
      }
    }
    if (!this.canAfford(item)) return { ok: false, reason: 'not-enough-coupons' }

    this.coupons -= item.cost
    if (item.kind === 'weapon') this.weapons.add(item.id)
    if (item.kind === 'food') this.pantry.set(item.id, (this.pantry.get(item.id) ?? 0) + 1)
    if (item.kind === 'powerup') this.powerUpRemaining = POWER_UP_SECONDS
    return { ok: true }
  }

  /** "To collect coupons, you run through them." */
  collect(amount: number): void {
    this.coupons += amount
  }

  /**
   * Charge the respawn. Kate said you lose 50 — if you have fewer, you lose what you
   * have rather than going into debt, since she never described negative coupons.
   */
  chargeRespawn(): number {
    const charged = Math.min(RESPAWN_COST, this.coupons)
    this.coupons -= charged
    return charged
  }

  /** Bonus damage from the best weapon owned, plus the power-up. */
  bonusStrength(): number {
    let best = 0
    for (const id of this.weapons) best = Math.max(best, WEAPON_STRENGTH[id] ?? 0)
    return best + (this.powerUpRemaining > 0 ? POWER_UP_STRENGTH : 0)
  }

  jumpMultiplier(): number {
    return this.powerUpRemaining > 0 ? POWER_UP_JUMP_MULTIPLIER : 1
  }

  get powerUpActive(): boolean {
    return this.powerUpRemaining > 0
  }

  update(dt: number): void {
    if (this.powerUpRemaining <= 0) return
    this.powerUpRemaining -= dt
    // Summing dt over a full 26-minute day leaves a float crumb, which kept the
    // power-up reading as active forever at an apparent 0 seconds remaining.
    if (this.powerUpRemaining < 1e-6) this.powerUpRemaining = 0
  }

  /** Eat one item from the pantry. Returns hunger levels restored, or 0. */
  eat(id: string): number {
    const count = this.pantry.get(id) ?? 0
    if (count <= 0) return 0
    const item = FOOD.find((f) => f.id === id)
    if (!item) return 0
    if (count === 1) this.pantry.delete(id)
    else this.pantry.set(id, count - 1)
    return item.feeds ?? 0
  }

  /** A new game: back to the starting purse with nothing owned. */
  reset(): void {
    this.coupons = STARTING_COUPONS
    this.weapons.clear()
    this.pantry.clear()
    this.powerUpRemaining = 0
  }
}
